

import React, { useState, useEffect, useRef } from "react";
import { ref, get, set, database } from "./firebase";
import {
  FiSearch,
  FiUpload,
  FiLogOut,
  FiGift,
  FiX,
  FiVideo,
  FiMenu,
} from "react-icons/fi";
import { FaRupeeSign } from "react-icons/fa";
import { RiAccountCircleFill } from "react-icons/ri";
import { useNavigate } from "react-router-dom";
import { storage, storageRef, getDownloadURL } from "./firebase";
import { useSearch } from "./SearchContext";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "react-toastify";
import { searchYouTubeVideos } from "./Pages/YoutubeAPI";

const Navbar = () => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [showRewardsPopup, setShowRewardsPopup] = useState(false);
  const [profileImage, setProfileImage] = useState(null);
  const [points, setPoints] = useState(0);
  const [userMobile, setUserMobile] = useState(null);
  const [withdrawPoints, setWithdrawPoints] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const navigate = useNavigate();
  const {
    searchQuery,
    updateSearchQuery,
    setIsSearching,
    setEmbedType,
    clearEmbedType,
    updateYoutubeResults,
  } = useSearch();
  const searchRef = useRef(null);

  useEffect(() => {
    const currentUser = JSON.parse(localStorage.getItem("currentUser"));
    if (currentUser && currentUser.mobile) {
      setUserMobile(currentUser.mobile);
      fetchPoints(currentUser.mobile);
      fetchProfileImage(currentUser);
    }
  }, []);

  const fetchPoints = async (mobile) => {
    const sanitizedMobile = mobile.replace(/[^0-9]/g, "_");
    const pointsRef = ref(database, `videoplayer/Users/${sanitizedMobile}`);
    try {
      const snapshot = await get(pointsRef);
      setPoints(snapshot.exists() ? snapshot.val().points || 0 : 0);
    } catch (err) {
      console.error("Error fetching points:", err);
      setPoints(0);
    }
  };

  const fetchProfileImage = async (currentUser) => {
    if (currentUser.profilePictureUrl) {
      setProfileImage(currentUser.profilePictureUrl);
    } else if (currentUser.email) {
      const emailKey = currentUser.email.replace(/\./g, "_");
      const extensions = ["jpg", "jpeg", "png"];
      for (const ext of extensions) {
        try {
          const url = await getDownloadURL(
            storageRef(storage, `videoplayer/UserProfile/${emailKey}.${ext}`)
          );
          setProfileImage(url);
          localStorage.setItem(
            "currentUser",
            JSON.stringify({
              ...currentUser,
              profilePictureUrl: url,
            })
          );
          break;
        } catch (err) {}
      }
    }
  };

  useEffect(() => {
    const trimmedQuery = searchQuery.trim().toLowerCase();
    if (trimmedQuery) {
      const suggestionList = [];
      if ("google".startsWith(trimmedQuery)) {
        suggestionList.push({ label: "Google", url: "https://www.google.com" });
      }
      // if ("youtube".startsWith(trimmedQuery)) {
      //   suggestionList.push({ label: "YouTube", url: "https://www.youtube.com" });
      // }
      setSuggestions(suggestionList);
      setShowSuggestions(suggestionList.length > 0);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
      setIsSearching(false);
    }
  }, [searchQuery]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSearch = async (e) => {
    e.preventDefault();
    const trimmedQuery = searchQuery.trim();

    if (!trimmedQuery) {
      setIsSearching(false);
      setShowSuggestions(false);
      clearEmbedType();
      return;
    }

    try {
      setEmbedType(null);
      setIsSearching(true);
      setShowSuggestions(false);
      const ytResults = await searchYouTubeVideos(trimmedQuery, 12);
      updateYoutubeResults(ytResults);
      navigate("/home", { state: { searchQuery: trimmedQuery } });
      setIsMenuOpen(false);
    } catch (error) {
      console.error("Search error:", error);
      setIsSearching(false);
      setShowSuggestions(false);
      toast.error("An error occurred during search. Please try again.");
    }
  };

  const handleSuggestionClick = (url) => {
    setShowSuggestions(false);
    setIsSearching(false);
    updateSearchQuery("");
    setIsMenuOpen(false);
    if (url.includes("google.com")) {
      setEmbedType("google");
      navigate("/home");
    } else if (url.includes("youtube.com")) {
      setEmbedType("youtube");
      navigate("/home");
    } else {
      window.location.href = url;
    }
  };

  const handleSignIn = () => {
    setShowDropdown(false);
    setIsMenuOpen(false);
    navigate("/");
  };

  const handleProfile = () => {
    setShowDropdown(false);
    setIsMenuOpen(false);
    navigate(userMobile ? "/profile" : "/");
  };

  const handleEarnPointsClick = () => {
    if (userMobile) {
      setShowRewardsPopup(true);
    } else {
      navigate("/");
    }
    setIsMenuOpen(false);
  };

  const handleLogout = () => {
    localStorage.removeItem("currentUser");
    setUserMobile(null);
    setProfileImage(null);
    setPoints(0);
    setShowDropdown(false);
    setIsMenuOpen(false);
    navigate("/login");
  };

  const handleClearSearch = () => {
    updateSearchQuery("");
    setIsSearching(false);
    setShowSuggestions(false);
    clearEmbedType();
  };

  const handleWithdraw = async () => {
    if (!userMobile) {
      toast.error("Please sign in to request a withdrawal.");
      navigate("/");
      return;
    }

    const pointsToWithdraw = parseInt(withdrawPoints, 10);
    if (!pointsToWithdraw || pointsToWithdraw <= 0) {
      toast.error("Please enter a valid number of points to withdraw.");
      return;
    }

    if (pointsToWithdraw > points) {
      toast.error("Insufficient points for withdrawal.");
      return;
    }

    const sanitizedMobile = userMobile.replace(/[^0-9]/g, "_");
    const userRef = ref(database, `videoplayer/Users/${sanitizedMobile}`);
    const userWithdrawRequestsRef = ref(
      database,
      `videoplayer/withdraw/${sanitizedMobile}`
    );

    try {
      const userSnapshot = await get(userRef);
      if (!userSnapshot.exists()) {
        toast.error("User data not found. Please sign in again.");
        navigate("/");
        return;
      }

      const userData = userSnapshot.val();
      const withdrawSnapshot = await get(userWithdrawRequestsRef);
      const withdrawRequests = withdrawSnapshot.exists()
        ? withdrawSnapshot.val()
        : {};
      const sequenceNumbers = Object.keys(withdrawRequests)
        .map((key) => parseInt(key, 10))
        .filter((num) => !isNaN(num));
      const nextSequence =
        sequenceNumbers.length > 0 ? Math.max(...sequenceNumbers) + 1 : 1;

      const newWithdrawRef = ref(
        database,
        `videoplayer/withdraw/${sanitizedMobile}/${nextSequence}`
      );
      await set(newWithdrawRef, {
        mobile: userMobile,
        name: userData.name || "N/A",
        surname: userData.surname || "N/A",
        withdrawPoints: pointsToWithdraw,
        timestamp: new Date().toISOString(),
      });

      await set(userRef, {
        ...userData,
        points: (userData.points || 0) - pointsToWithdraw,
      });

      setPoints((prevPoints) => prevPoints - pointsToWithdraw);
      setWithdrawPoints("");
      toast.success(
        `Successfully requested withdrawal of ${pointsToWithdraw} points!`
      );
      setShowRewardsPopup(false);
      navigate("/task");
    } catch (err) {
      console.error("Error handling withdraw:", err);
      toast.error("Failed to submit withdrawal request. Please try again.");
    }
  };

  const MoneyRain = () => {
    const [moneyDrops, setMoneyDrops] = useState([]);

    useEffect(() => {
      const createDrop = () => ({
        id: Math.random().toString(36).substr(2, 9),
        left: Math.random() * 100,
        delay: Math.random() * 2,
        duration: 3 + Math.random() * 2,
        size: 12 + Math.random() * 8,
        opacity: 0.7 + Math.random() * 0.3,
      });

      const interval = setInterval(() => {
        setMoneyDrops((prev) => [...prev.slice(-10), createDrop()]);
      }, 300);

      return () => clearInterval(interval);
    }, []);

    return (
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {moneyDrops.map((drop) => (
          <motion.div
            key={drop.id}
            className="absolute text-yellow-400"
            style={{
              left: `${drop.left}%`,
              top: "-20px",
              fontSize: `${drop.size}px`,
              opacity: drop.opacity,
            }}
            initial={{ y: -20, rotate: 0 }}
            animate={{
              y: "100vh",
              rotate: 360,
              transition: {
                duration: drop.duration,
                delay: drop.delay,
                repeat: Infinity,
                ease: "linear",
              },
            }}
          >
            💵
          </motion.div>
        ))}
      </div>
    );
  };

  return (
    <nav className="bg-white dark:bg-gray-900 shadow-md fixed w-full z-20 top-0 border-b border-gray-200 dark:border-gray-800">
      <div className="w-full mx-auto px-2 sm:px-6 lg:px-8 mb-1">
        <div className="flex justify-between items-center h-16 sm:bg-white sm:dark:bg-gray-900">
          {/* Mobile header background */}
          <div className="absolute inset-0 sm:hidden bg-red-600 -z-10"></div>
          {/* Left: Hamburger on mobile, Logo on desktop */}
          <div className="flex items-center min-w-0">
            {/* Hamburger Menu Button - Visible on mobile */}
            <div className="sm:hidden flex items-center">
              <button
                className="p-2 rounded-full hover:bg-red-700 focus:outline-none transition-all duration-300 text-white"
                onClick={() => {
                  setIsMenuOpen(!isMenuOpen);
                  setShowMobileSearch(false);
                }}
              >
                <FiMenu className="h-6 w-6 text-white" />
              </button>
            </div>
            {/* Mobile: Show logo and heading, only on mobile */}
            <button
              className="flex sm:hidden items-center text-white hover:text-gray-200 transition-all duration-300 hover:scale-105 ml-2"
              onClick={() => navigate("/home")}
            >
              <FiVideo className="h-6 w-6" />
              <span className="ml-1 text-[16px] font-bold bg-clip-text truncate">
                TimePassServices
              </span>
            </button>
            {/* Desktop: Show logo/video icon and text, hide on mobile */}
            <button
              className="hidden sm:flex items-center text-indigo-600 hover:text-indigo-700 dark:hover:text-indigo-500 transition-all duration-300 hover:scale-105 ml-2"
              onClick={() => navigate("/home")}
            >
              <FiVideo className="h-6 w-6 sm:h-8 sm:w-8" />
              <span className="ml-1 text-lg sm:text-xl font-bold hidden sm:block bg-gradient-to-r from-indigo-600 to-indigo-800 dark:from-indigo-500 dark:to-indigo-700 bg-clip-text text-transparent">
                TimePassServices
              </span>
            </button>
          </div>

          {/* Search Bar - Only visible on sm and above */}
          <div
            className="hidden sm:flex flex-1 max-w-xl mx-4 relative"
            ref={searchRef}
           >
            <form onSubmit={handleSearch} className="flex relative w-full">
              <input
                type="text"
                placeholder="Search Google, YouTube, Video, Photo..."
                className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 dark:border-gray-700 rounded-l-full focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:focus:ring-indigo-600 transition-all duration-300 bg-white dark:bg-gray-800 dark:text-white pr-10"
                value={searchQuery}
                onChange={(e) => {
                  updateSearchQuery(e.target.value);
                }}
                onFocus={() => {
                  if (suggestions.length > 0) setShowSuggestions(true);
                }}
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={handleClearSearch}
                  className="absolute right-12 top-1/2 transform -translate-y-1/2 p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 focus:outline-none"
                >
                  <FiX className="h-4 w-4 sm:h-5 sm:w-5 text-gray-500 dark:text-gray-300" />
                </button>
              )}
              <button
                type="submit"
                className="px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-l-0 border-gray-300 dark:border-gray-700 rounded-r-full hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none transition-all duration-300 hover:scale-105"
              >
                <FiSearch className="h-4 w-4 sm:h-5 sm:w-5 text-gray-600 dark:text-gray-300" />
              </button>
            </form>
            <AnimatePresence>
              {showSuggestions && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="absolute left-0 right-0 mt-1 bg-white dark:bg-gray-800 rounded-lg shadow-xl z-10 border border-gray-200 dark:border-gray-700 overflow-hidden w-full max-w-full"
                >
                  {suggestions.map((suggestion, index) => (
                    <button
                      key={index}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-indigo-100 dark:hover:bg-indigo-900/20 transition-colors duration-200 flex items-center truncate"
                      onClick={() => handleSuggestionClick(suggestion.url)}
                    >
                      <FiSearch className="h-4 w-4 mr-2 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
                      {suggestion.label}
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Right Side: Desktop icons, Mobile search/earn points */}
          <div className="flex items-center space-x-2 sm:space-x-3 min-w-0">
            {/* Mobile: Search and Earn Points on right */}
            <div className="sm:hidden flex items-center space-x-2">
              <button
                className="flex items-center text-white hover:text-gray-200 transition-all duration-300 hover:scale-105"
                onClick={() => {
                  setShowMobileSearch(true);
                  setIsMenuOpen(false);
                }}
              >
                <FiSearch className="h-6 w-6" />
              </button>
              <button
                className="flex items-center px-2 py-1 rounded-full bg-amber-100 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 text-xs font-semibold hover:bg-amber-200 dark:hover:bg-amber-900/40 transition-all duration-200"
                onClick={handleEarnPointsClick}
                style={{ height: "32px" }}
                title="Earn Points"
              >
                <FaRupeeSign className="h-4 w-4 mr-1 text-amber-600 dark:text-amber-400 flex-shrink-0" />
                <span>{points}</span>
              </button>
            </div>
            {/* Desktop: Right side icons */}
            <div className="hidden sm:flex items-center space-x-3">
              {/* Upload Button */}
              <button
                className="p-2 rounded-full hover:bg-indigo-100 dark:hover:bg-indigo-900/20 focus:outline-none transition-all duration-300 hover:scale-110 active:scale-95 group"
                onClick={() =>
                  userMobile ? navigate("/upload") : navigate("/")
                }
                title="Upload"
              >
                <FiUpload className="h-5 w-5 sm:h-6 sm:w-6 text-gray-700 dark:text-gray-300 group-hover:text-indigo-600 dark:group-hover:text-indigo-400" />
              </button>

              {/* Earn Points Button */}
              <div className="relative">
                <button
                  className="p-2 rounded-full hover:bg-amber-100/50 dark:hover:bg-amber-900/20 focus:outline-none transition-all duration-300 group relative overflow-hidden"
                  onClick={handleEarnPointsClick}
                  title="Earn Points"
                >
                  <motion.div
                    className="absolute inset-0 rounded-full bg-amber-400/10"
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{
                      scale: 1.5,
                      opacity: [0, 0.2, 0],
                      transition: {
                        duration: 3,
                        repeat: Infinity,
                        ease: "easeInOut",
                      },
                    }}
                  />
                  <div className="relative">
                    <motion.div
                      className="relative"
                      whileHover={{ rotate: 15, scale: 1.1 }}
                      whileTap={{ rotate: -15, scale: 0.9 }}
                      transition={{
                        type: "spring",
                        stiffness: 300,
                        damping: 10,
                      }}
                    >
                      <FaRupeeSign className="h-5 w-5 sm:h-6 sm:w-6 text-amber-600 dark:text-amber-400 group-hover:text-amber-500 dark:group-hover:text-amber-300" />
                      <motion.div
                        className="absolute inset-0 bg-white/30"
                        initial={{ left: "-100%" }}
                        animate={{
                          left: "100%",
                          transition: {
                            duration: 1.5,
                            repeat: Infinity,
                            ease: "easeInOut",
                          },
                        }}
                      />
                    </motion.div>
                    <motion.span
                      className="absolute -top-2 -right-2 bg-gradient-to-br from-amber-500 to-yellow-500 dark:from-amber-600 dark:to-yellow-600 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center shadow-lg group-hover:from-amber-600 group-hover:to-yellow-600 dark:group-hover:from-amber-700 dark:group-hover:to-yellow-700 transition-all duration-300"
                      whileHover={{ scale: 1.2, rotate: 10 }}
                      whileTap={{ scale: 0.9 }}
                      transition={{
                        type: "spring",
                        stiffness: 400,
                        damping: 10,
                      }}
                    >
                      {points}
                    </motion.span>
                    <motion.div
                      className="absolute -top-4 -left-4 w-full h-full pointer-events-none"
                      initial={{ opacity: 0 }}
                      whileHover={{
                        opacity: 1,
                        transition: { staggerChildren: 0.1 },
                      }}
                    >
                      {[0, 1, 2].map((i) => (
                        <motion.span
                          key={i}
                          className="absolute text-amber-500 text-xs"
                          initial={{ y: 0, x: 0, opacity: 0 }}
                          animate={{
                            y: -15 - i * 5,
                            x: i % 2 === 0 ? -5 : 5,
                            opacity: [0, 1, 0],
                            transition: {
                              duration: 1.5,
                              repeat: Infinity,
                              delay: i * 0.3,
                            },
                          }}
                        >
                          ₹
                        </motion.span>
                      ))}
                    </motion.div>
                  </div>
                </button>
              </div>

              {/* User Profile Dropdown */}
              <div className="relative">
                <button
                  className="flex items-center space-x-1 focus:outline-none transition-all duration-300 hover:scale-105 group"
                  onClick={() => setShowDropdown(!showDropdown)}
                >
                  <div className="h-7 w-7 sm:h-8 sm:w-8 rounded-full bg-gradient-to-br from-indigo-500 to-indigo-700 dark:from-indigo-600 dark:to-indigo-800 flex items-center justify-center text-white overflow-hidden shadow-md group-hover:from-indigo-600 group-hover:to-indigo-800 dark:group-hover:from-indigo-700 dark:group-hover:to-indigo-900">
                    {profileImage ? (
                      <img
                        src={profileImage}
                        alt="User Profile"
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <RiAccountCircleFill className="h-6 w-6 sm:h-7 sm:w-7" />
                    )}
                  </div>
                </button>
                <AnimatePresence>
                  {showDropdown && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-xl py-1 z-20 border border-gray-200 dark:border-gray-700"
                    >
                      {userMobile ? (
                        <>
                          <button
                            className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-indigo-100 dark:hover:bg-indigo-900/20 transition-colors duration-200 flex items-center"
                            onClick={handleProfile}
                          >
                            <span className="bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 p-1 rounded mr-2">
                              <RiAccountCircleFill className="h-4 w-4" />
                            </span>
                            My Profile
                          </button>
                          <button
                            className="block w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-indigo-100 dark:hover:bg-indigo-900/20 border-t border-gray-200 dark:border-gray-700 transition-colors duration-200 flex items-center"
                            onClick={handleLogout}
                          >
                            <span className="bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 p-1 rounded mr-2">
                              <FiLogOut className="h-4 w-4" />
                            </span>
                            Logout
                          </button>
                        </>
                      ) : (
                        <button
                          className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-indigo-100 dark:hover:bg-indigo-900/20 transition-colors duration-200 flex items-center"
                          onClick={handleSignIn}
                        >
                          <span className="bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 p-1 rounded mr-2">
                            <RiAccountCircleFill className="h-4 w-4" />
                          </span>
                          Sign In
                        </button>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Menu - Shown when hamburger is clicked */}
        <AnimatePresence>
          {(isMenuOpen || showMobileSearch) && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="sm:hidden bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 w-full"
            >
              <div className="px-2 py-3">
                {showMobileSearch ? (
                  <>
                    <form
                      onSubmit={handleSearch}
                      className="flex relative mb-3 w-full"
                      ref={searchRef}
                    >
                      <input
                        type="text"
                        placeholder="Search..."
                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded-l-full focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:focus:ring-indigo-600 transition-all duration-300 bg-white dark:bg-gray-800 dark:text-white pr-10"
                        value={searchQuery}
                        onChange={(e) => {
                          updateSearchQuery(e.target.value);
                        }}
                        onFocus={() => {
                          if (suggestions.length > 0) setShowSuggestions(true);
                        }}
                      />
                      {/* {searchQuery && (
                        <button
                          type="button"
                          onClick={handleClearSearch}
                          className="absolute right-12 top-1/2 transform -translate-y-1/2 p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 focus:outline-none"
                        >
                          <FiX className="h-4 w-4 text-gray-500 dark:text-gray-300" />
                        </button>
                      )} */}
                      <button
                        type="submit"
                        className="px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-l-0 border-gray-300 dark:border-gray-700 rounded-r-full hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none transition-all duration-300"
                      >
                        <FiSearch className="h-4 w-4 text-gray-600 dark:text-gray-300" />
                      </button>
                      {/* Close search bar button */}
                      <button
                        type="button"
                        onClick={() => setShowMobileSearch(false)}
                        className="ml-2 p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 focus:outline-none flex-shrink-0"
                      >
                        <FiX className="h-5 w-5 text-gray-500 dark:text-gray-300" />
                      </button>
                    </form>
                    <AnimatePresence>
                      {showSuggestions && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          className="relative mb-3 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden w-full max-w-full"
                        >
                          {suggestions.map((suggestion, index) => (
                            <button
                              key={index}
                              className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-indigo-100 dark:hover:bg-indigo-900/20 transition-colors duration-200 flex items-center truncate"
                              onClick={() =>
                                handleSuggestionClick(suggestion.url)
                              }
                            >
                              <FiSearch className="h-4 w-4 mr-2 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
                              {suggestion.label}
                            </button>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </>
                ) : (
                  <div className="flex flex-col space-y-2 w-full">
                    <button
                      className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-indigo-100 dark:hover:bg-indigo-900/20 rounded-lg w-full truncate"
                      onClick={() =>
                        userMobile ? navigate("/upload") : navigate("/")
                      }
                    >
                      <FiUpload className="h-5 w-5 mr-2 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
                      Upload
                    </button>
                    <button
                      className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-indigo-100 dark:hover:bg-indigo-900/20 rounded-lg relative w-full truncate"
                      onClick={handleEarnPointsClick}
                    >
                      <motion.div
                        className="absolute inset-0 rounded-lg bg-amber-400/10"
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{
                          scale: 1.5,
                          opacity: [0, 0.2, 0],
                          transition: {
                            duration: 3,
                            repeat: Infinity,
                            ease: "easeInOut",
                          },
                        }}
                      />
                      <div className="relative flex items-center">
                        <FaRupeeSign className="h-5 w-5 mr-2 text-amber-600 dark:text-amber-400 flex-shrink-0" />
                        Earn Points
                        <motion.span
                          className="ml-2 bg-gradient-to-br from-amber-500 to-yellow-500 dark:from-amber-600 dark:to-yellow-600 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center shadow-lg"
                          whileHover={{ scale: 1.2, rotate: 10 }}
                          whileTap={{ scale: 0.9 }}
                          transition={{
                            type: "spring",
                            stiffness: 400,
                            damping: 10,
                          }}
                        >
                          {points}
                        </motion.span>
                      </div>
                    </button>
                    {userMobile ? (
                      <>
                        <button
                          className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-indigo-100 dark:hover:bg-indigo-900/20 rounded-lg w-full truncate"
                          onClick={handleProfile}
                        >
                          <RiAccountCircleFill className="h-5 w-5 mr-2 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
                          My Profile
                        </button>
                        <button
                          className="flex items-center px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-indigo-100 dark:hover:bg-indigo-900/20 rounded-lg w-full truncate"
                          onClick={handleLogout}
                        >
                          <FiLogOut className="h-5 w-5 mr-2 flex-shrink-0" />
                          Logout
                        </button>
                      </>
                    ) : (
                      <button
                        className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-indigo-100 dark:hover:bg-indigo-900/20 rounded-lg w-full truncate"
                        onClick={handleSignIn}
                      >
                        <RiAccountCircleFill className="h-5 w-5 mr-2 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
                        Sign In
                      </button>
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Golden Rewards Popup */}
      <AnimatePresence>
        {showRewardsPopup && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-30 px-2">
            <MoneyRain />
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", damping: 20, stiffness: 300 }}
              className="relative bg-gradient-to-br from-yellow-50 to-amber-50 dark:from-gray-800 dark:to-gray-900 rounded-xl shadow-2xl p-4 w-full max-w-md border-2 border-amber-200 dark:border-amber-800 overflow-hidden"
            >
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-amber-400 via-yellow-500 to-amber-400"></div>
              <div className="absolute -top-10 -right-10 w-16 h-16 rounded-full bg-amber-400/10 blur-xl"></div>
              <div className="absolute -bottom-10 -left-10 w-16 h-16 rounded-full bg-amber-400/10 blur-xl"></div>
              <div className="relative z-10">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-lg font-bold text-amber-800 dark:text-amber-400 flex items-center">
                    <FaRupeeSign className="h-6 w-6 text-amber-600 dark:text-amber-400 mr-2 bg-amber-100 dark:bg-amber-900/30 p-1.5 rounded-full" />
                    <span className="bg-clip-text text-transparent bg-gradient-to-r from-amber-600 to-yellow-600 dark:from-amber-400 dark:to-yellow-400">
                      Your Treasure
                    </span>
                  </h2>
                  <button
                    onClick={() => setShowRewardsPopup(false)}
                    className="p-1.5 rounded-full hover:bg-amber-100 dark:hover:bg-amber-900/20 transition-all duration-200 text-amber-600 dark:text-amber-400"
                  >
                    <FiX className="h-4 w-4" />
                  </button>
                </div>
                <div className="text-center mb-4">
                  <div className="inline-block relative">
                    <div className="absolute -inset-4 bg-amber-400/20 rounded-full animate-pulse-slow"></div>
                    <div className="relative bg-gradient-to-br from-amber-100 to-yellow-100 dark:from-gray-700 dark:to-gray-800 p-4 rounded-xl shadow-inner border border-amber-200 dark:border-amber-800">
                      <div className="text-4xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-amber-600 to-yellow-600 dark:from-amber-400 dark:to-yellow-400">
                        {points}
                      </div>
                      <div className="text-xs uppercase tracking-wider text-amber-600 dark:text-amber-400 mt-1">
                        Golden Points
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-amber-700 dark:text-amber-300 mb-2">
                    Points to Withdraw
                  </label>
                  <input
                    type="number"
                    value={withdrawPoints}
                    onChange={(e) => setWithdrawPoints(e.target.value)}
                    placeholder="Enter points to withdraw"
                    className="w-full px-3 py-2 text-sm border border-amber-300 dark:border-amber-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 dark:focus:ring-amber-600 bg-white dark:bg-gray-800 dark:text-white transition-all duration-300"
                    min="1"
                    max={points}
                  />
                </div>
                <div className="mb-4">
                  <div className="flex justify-between text-xs text-amber-700 dark:text-amber-300 mb-1">
                    <span>Your Progress</span>
                    <span>{points}/1000 pts</span>
                  </div>
                  <div className="h-3 w-full bg-amber-100 dark:bg-amber-900/20 rounded-full overflow-hidden shadow-inner">
                    <motion.div
                      className="h-full bg-gradient-to-r from-amber-400 to-yellow-500 rounded-full relative"
                      initial={{ width: 0 }}
                      animate={{
                        width: `${Math.min(100, (points / 1000) * 100)}%`,
                      }}
                      transition={{ duration: 1, ease: "easeOut" }}
                    >
                      <div className="absolute inset-0 bg-white/30 animate-shine"></div>
                    </motion.div>
                  </div>
                </div>
                <div className="space-y-3 mb-4">
                  <div className="flex items-start p-3 bg-amber-50 dark:bg-amber-900/10 rounded-lg border border-amber-200 dark:border-amber-800">
                    <div className="bg-amber-100 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 p-2 rounded-full mr-3">
                      <FaRupeeSign className="h-4 w-4" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-amber-800 dark:text-amber-300 text-sm">
                        Earn More Points
                      </h4>
                      <p className="text-xs text-amber-600/80 dark:text-amber-400/80">
                        Watch videos, upload content, and complete tasks to earn.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start p-3 bg-amber-50 dark:bg-amber-900/10 rounded-lg border border-amber-200 dark:border-amber-800">
                    <div className="bg-amber-100 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 p-2 rounded-full mr-3">
                      <FiGift className="h-4 w-4" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-amber-800 dark:text-amber-300 text-sm">
                        Withdraw Treasures
                      </h4>
                      <p className="text-xs text-amber-600/80 dark:text-amber-400/80">
                        Exchange points for exclusive rewards and gift cards.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    className="py-2 px-3 text-sm bg-amber-100 hover:bg-amber-200 dark:bg-amber-900/30 dark:hover:bg-amber-900/40 text-amber-700 dark:text-amber-300 font-medium rounded-lg border border-amber-300 dark:border-amber-700 transition-all duration-300 hover:shadow-md flex items-center justify-center"
                    onClick={() => navigate("/home")}
                  >
                    <FaRupeeSign className="mr-2 h-4 w-4" />
                    Earn More
                  </button>
                  <button
                    className="py-2 px-3 text-sm bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-white font-bold rounded-lg shadow-md transition-all duration-300 hover:shadow-lg flex items-center justify-center"
                    onClick={handleWithdraw}
                  >
                    <FiGift className="mr-2 h-4 w-4" />
                    Withdraw Now
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <style jsx>{`
        @keyframes pulse-slow {
          0%,
          100% {
            opacity: 0.2;
          }
          50% {
            opacity: 0.4;
          }
        }
        @keyframes shine {
          0% {
            left: -100%;
          }
          100% {
            left: 100%;
          }
        }
        .animate-pulse-slow {
          animation: pulse-slow 3s ease-in-out infinite;
        }
        .animate-shine {
          animation: shine 1.5s ease-in-out infinite;
        }
        .navbar-container {
          padding-left: 0.5rem;
          padding-right: 0.5rem;
        }
        @media (max-width: 640px) {
          .navbar-container {
            padding-left: 0.5rem;
            padding-right: 0.5rem;
          }
        }
        @media (max-width: 360px) {
          .navbar-container {
            padding-left: 0.25rem;
            padding-right: 0.25rem;
          }
        }
      `}</style>
    </nav>
  );
};

export default Navbar;